import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { formStyles } from '../utils/formStyles';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL;

export const NewsFormFields = forwardRef(({ formData, handleChange, handleFileChange, handleSubmit, isEdit = false, onCancel, onRemoveImage, onRemoveVideo, isSubmitting, uploadProgress = 0, isProcessingFiles = false }, ref) => {
  const { t } = useTranslation();
  const imageInputRef = useRef();
  const videoInputRef = useRef();

  // Expose reset method to parent
  useImperativeHandle(ref, () => ({
    resetFileInputs: () => {
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  }));

  // Helper for image preview
  const getImagePreview = (img) => {
    if (!img) return null;
    if (typeof img === 'string') {
      return img.startsWith('http') ? img : `${API_URL}${img}`;
    }
    if (img instanceof File) return URL.createObjectURL(img);
    return null;
  };
  // Helper for video preview
  const getVideoPreview = (vid) => {
    if (!vid) return null;
    if (typeof vid === 'string') {
      return vid.startsWith('http') ? vid : `${API_URL}${vid}`;
    }
    if (vid instanceof File) return URL.createObjectURL(vid);
    return null;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* HEIC Processing Overlay */}
      {isProcessingFiles && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-zinc-800 rounded-xl p-8 shadow-xl flex flex-col items-center gap-4 min-w-[280px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              HEIC-Bilder werden konvertiert...
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              iOS-Fotos werden zu JPEG umgewandelt
            </p>
          </div>
        </div>
      )}
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-zinc-800 rounded-xl p-8 shadow-xl flex flex-col items-center gap-4 min-w-[280px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            {uploadProgress < 100 ? (
              <>
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  News wird hochgeladen ...
                </p>
                <div className="w-full flex flex-col items-center gap-2">
                  <div className="w-48 h-3 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">{uploadProgress}%</div>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  Upload abgeschlossen. Video/Bilder werden verarbeitet ...
                </p>
              </>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <textarea
            id="text_de"
            name="text_de"
            value={formData.text_de || ''}
            onChange={handleChange}
            className={formStyles.input + ' min-h-[80px]'}
            required
          />
          <label htmlFor="text_de" className={`${formStyles.label} ${formData.text_de ? formStyles.labelActive : ''}`}>{t('news.text_de')}</label>
        </div>
        <div className="relative">
          <textarea
            id="text_en"
            name="text_en"
            value={formData.text_en || ''}
            onChange={handleChange}
            className={formStyles.input + ' min-h-[80px]'}
            required
          />
          <label htmlFor="text_en" className={`${formStyles.label} ${formData.text_en ? formStyles.labelActive : ''}`}>{t('news.text_en')}</label>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-semibold text-gray-700 dark:text-gray-200">{t('news.image')}</label>
          <input
            type="file"
            accept="image/*,.heic,.heif"
            ref={imageInputRef}
            multiple
            onChange={e => handleFileChange(e, 'images')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {Array.isArray(formData.images) && formData.images.map((img, idx) => (
              <div key={idx} className="relative group">
                <img src={getImagePreview(img)} alt="Preview" className="w-20 h-20 object-cover rounded border" />
                <button type="button" onClick={() => onRemoveImage && onRemoveImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-80 group-hover:opacity-100" title="Entfernen">×</button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="block mb-1 font-semibold text-gray-700 dark:text-gray-200">{t('news.video')}</label>
          <input
            type="file"
            accept="video/*"
            ref={videoInputRef}
            multiple
            onChange={e => handleFileChange(e, 'videos')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {Array.isArray(formData.videos) && formData.videos.map((vid, idx) => (
              <div key={idx} className="relative group">
                <video src={getVideoPreview(vid)} controls className="w-24 h-20 object-cover rounded border" />
                <button type="button" onClick={() => onRemoveVideo && onRemoveVideo(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-80 group-hover:opacity-100" title="Entfernen">×</button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-4">
        <button type="submit" className={formStyles.button}>
          {isEdit ? t('news.update') : t('news.create')}
        </button>
        {isEdit && onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 dark:bg-zinc-700 rounded text-gray-800 dark:text-gray-200">
            {t('news.cancel')}
          </button>
        )}
      </div>
    </form>
  );
});